import type { FC } from 'react'

import { StyledText } from '@skytech/ui'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import { selectedStyleByWhiteLabel } from '@/components/util/style-constant'
import { useCart } from '@/contexts/cart/cart-context'

import PolicyLink from './PolicyLink'
import { PolicyPictures } from './PolicyPictures'

export const PolicyListItem: FC<{
  id: number
  name: string
  description: string
  isLocked: number
  isOffboardingBlocked?: boolean
  isProductViewAllowedWhenLocked: boolean
  nextOrderDate: string
  images: string[]
}> = ({
  id,
  name,
  description,
  isLocked,
  isOffboardingBlocked,
  isProductViewAllowedWhenLocked,
  nextOrderDate,
  images,
}) => {
  const { whiteLabel } = useAppContext()
  const { getItemsByPolicy } = useCart()
  const itemsCount = getItemsByPolicy(id).length
  const policyHasSelectedItems = itemsCount > 0
  const isLockedPolicy = Boolean(isLocked && !isProductViewAllowedWhenLocked)

  return (
    <PolicyLink id={id} isLocked={isLockedPolicy}>
      <li
        className={twJoin(
          'shadow-basic-black border-1 mb-4 flex items-center justify-between rounded-lg border-solid transition duration-200 ease-out',
          isLockedPolicy ? 'opacity-70' : 'hover:scale-99 focus:scale-99 transition-transform',
          'relative',
          policyHasSelectedItems && !isLockedPolicy
            ? selectedStyleByWhiteLabel[whiteLabel]?.border ||
                selectedStyleByWhiteLabel.skytech!.border
            : 'border-transparent'
        )}
      >
        <div className="w-3/4 p-4 text-gray-800">
          <div className={twJoin('mb-1', isLockedPolicy && 'text-gray-700')}>
            <StyledText size="xl" fontWeight="thin" isDarkenedOnHover={!isLockedPolicy}>
              {name}
            </StyledText>
          </div>
          {/* If getItemsByPolicy has items, show a message that there are products selected from this policy in english */}
          {policyHasSelectedItems && (
            <div className="mb-1">
              <h4 className="m-0 p-0">
                <StyledText size="sm" fontWeight="bold">
                  {itemsCount}{' '}
                </StyledText>
                <StyledText colorMode="black" size="sm" fontWeight="standard">
                  <Translation
                    id="{count, plural, one {product} other {products}} selected"
                    count={itemsCount}
                  />
                </StyledText>
              </h4>
            </div>
          )}

          {isLocked ? (
            <div className="mt-1">
              {isOffboardingBlocked ? (
                <StyledText colorMode="black" size="sm">
                  <Translation id="Ordering is locked because offboarding is in progress" />
                </StyledText>
              ) : (
                <>
                  <StyledText colorMode="black" size="sm">
                    <Translation id="Bestilling åpner" />{' '}
                  </StyledText>
                  {nextOrderDate && <LocalDate date={nextOrderDate} />}
                </>
              )}
            </div>
          ) : (
            !policyHasSelectedItems && (
              <div className="mb-2 mt-1 sm:mb-3">
                <StyledText colorMode="black" size="sm">
                  {description}
                </StyledText>
              </div>
            )
          )}
        </div>
        <div>
          <PolicyPictures
            className={twJoin(isLocked && isProductViewAllowedWhenLocked && 'opacity-70')}
            images={images}
            isSelected={policyHasSelectedItems}
            isDisabled={Boolean(isLocked)}
          />
        </div>
      </li>
    </PolicyLink>
  )
}
